import { useEffect, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";

import { logger } from "@/utils/logger";
import { useAnnotationsQuery } from "./useLabelingQueries";
import { segmentationVolumeIdAtom } from "../atoms/segmentationAtoms";
import { stoneOverlayMeasurementAtom } from "../atoms/stoneOverlayAtoms";
import { loadMaskAsContours, computeMaxCaliperMm } from "../utils/niftiMaskLoader";
import { companionLabelOrders, labelOrdersOf } from "../utils/savedStoneOrder";
import { getCornerstone, getCornerstoneTools } from "../utils/dicomLoader";

/**
 * Headless reader for a saved labeling segmentation: when `studyUid` +
 * `projectId` are set, fetch that study's saved mask + `long_axis_mm` from the
 * labeling store and render the mask as a Cornerstone contour overlay across
 * the MPR panes — WITHOUT mounting the labeling panel. Used by the rdn60
 * reporter stone-overlay demo (`StoneOverlayLoader`).
 *
 * Read-only: this never draws or saves. It mirrors the primary-mask load in
 * `SegmentationAnnotationPanel` (poll for a ready viewport → `loadMaskAsContours`),
 * but as a self-contained hook so the labeling panel is untouched. The contour
 * segmentation representation it loads into is already set up unconditionally
 * by `HangingProtocolGrid` (gated only on `newViewportFlag`, not on labeling
 * mode), so this works in the reviewer/reporter views too.
 *
 * Pass `null` for either arg to disable (no fetch, no load) — e.g. for studies
 * not in the cherry-picked allowlist.
 */
export function useSavedMaskOverlay(studyUid: string | null, projectId: string | null): void {
  const enabled = !!studyUid && !!projectId;
  const annotationsQuery = useAnnotationsQuery(studyUid ?? "", projectId ?? "");
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const setMeasurement = useSetAtom(stoneOverlayMeasurementAtom);

  const maskLoadedRef = useRef(false);
  // Re-arm the one-shot load when the study changes.
  useEffect(() => {
    maskLoadedRef.current = false;
  }, [studyUid]);

  // Publish the longest dimension (max long_axis_mm across saved annotations)
  // as soon as the annotation lands; clear it on study change / unmount so it
  // can't leak into the next case.
  const annotations = annotationsQuery.data?.annotations;
  const maskUrl = annotationsQuery.data?.mask_url;
  useEffect(() => {
    if (!enabled || !studyUid) return;
    if (!annotations || annotations.length === 0) return;

    // Immediate fallback: manual long-axis if measured, else the convex-hull
    // longest axis (max across annotations for bilateral stones). The hull runs
    // ~10-20% large, so it's only used if the precise caliper can't be computed.
    let hullLongest = 0;
    for (const a of annotations) {
      const manual = a.measurements?.long_axis_mm ?? 0;
      const hull = a.hull_stats?.axis_1_mm ?? 0;
      const dim = manual > 0 ? manual : hull;
      if (dim > hullLongest) hullLongest = dim;
    }

    let cancelled = false;
    (async () => {
      // Prefer the true max-caliper (Feret) diameter computed from the primary
      // mask voxels — the accurate longest dimension.
      let caliper: number | null = null;
      if (maskUrl) {
        try {
          caliper = await computeMaxCaliperMm(maskUrl);
        } catch (e) {
          logger.warn("[StoneOverlay] max-caliper compute failed; using hull axis:", e);
        }
      }
      if (cancelled) return;
      const longest = caliper && caliper > 0 ? caliper : hullLongest;
      if (longest > 0) {
        logger.info(
          `[StoneOverlay] longest dimension = ${longest.toFixed(1)} mm (${caliper ? "max-caliper" : "hull fallback"}) for ${studyUid}`,
        );
        setMeasurement({ studyUid, longAxisMm: longest });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, studyUid, annotations, maskUrl, setMeasurement]);

  useEffect(() => {
    return () => setMeasurement(null);
  }, [studyUid, setMeasurement]);

  // Load the saved mask as contours once the gate + a ready viewport exist.
  useEffect(() => {
    if (!enabled) return;
    const maskUrl = annotationsQuery.data?.mask_url;
    if (!maskUrl || !segmentationVolumeId) return;
    if (maskLoadedRef.current) return;

    // Skip if contours are already on the canvas (loaded this session).
    try {
      const csTools = getCornerstoneTools();
      const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
      const existing = allAnns.filter(
        (a: any) =>
          a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          a.data?.contour?.polyline?.length >= 3,
      );
      if (existing.length > 0) {
        maskLoadedRef.current = true;
        return;
      }
    } catch (e) {
      logger.debug("[StoneOverlay] contour pre-check skipped (cornerstone not ready):", e);
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 50; // 50 × 200ms = 10s
    const annSeriesUid = annotations?.[0]?.series_instance_uid;
    // Per-companion poll timers, cancelled on unmount/study change.
    const companionPolls: Array<ReturnType<typeof setInterval>> = [];

    const poll = setInterval(() => {
      if (cancelled) {
        clearInterval(poll);
        return;
      }
      attempts++;
      try {
        const cornerstone = getCornerstone();
        const engines: any[] = cornerstone.getRenderingEngines?.() ?? [];

        let matchedViewport: any = null;
        let matchedElement: HTMLElement | null = null;

        // Strategy 1: match the annotation's series UID.
        if (annSeriesUid) {
          for (const engine of engines) {
            for (const vp of engine.getViewports?.() ?? []) {
              const ids: string[] = vp.getImageIds?.() ?? [];
              if (
                ids.length > 0 &&
                vp.getImageData?.()?.imageData &&
                ids[0]?.includes(annSeriesUid.split("::")[0])
              ) {
                matchedViewport = vp;
                matchedElement = vp.element ?? null;
                break;
              }
            }
            if (matchedViewport) break;
          }
        }

        // NO general fallback: the NIfTI mask is defined in the annotation
        // series' voxel grid, so loading it onto any other series mis-places
        // the contour (the labeling viewer and reporter show different series).
        // We only draw onto a viewport showing the exact annotation series;
        // the caller forces that series into a pane (see CaseViewer priority
        // override) so this match succeeds.
        if (!matchedViewport || !matchedElement) {
          if (attempts >= maxAttempts) {
            clearInterval(poll);
            logger.warn(
              `[StoneOverlay] annotation series ${annSeriesUid ?? "?"} not shown in any viewport after 10s — mask not loaded (avoided drawing on a mismatched series)`,
            );
          }
          return;
        }

        clearInterval(poll);
        const currentIdx = matchedViewport.getCurrentImageIdIndex?.() ?? 0;
        maskLoadedRef.current = true;

        loadMaskAsContours(maskUrl, segmentationVolumeId, matchedViewport, matchedElement, {
          labelOrders: labelOrdersOf(annotations ?? []),
        })
          .then((n) => {
            logger.info(`[StoneOverlay] loaded ${n} contour(s) for ${studyUid}`);
            // Jump the pane to the first slice that has a loaded contour so the
            // stone is immediately visible — the mask spans only a few slices
            // in the middle of the stack, so the default slice shows nothing.
            try {
              const csTools = getCornerstoneTools();
              const ids: string[] = matchedViewport.getImageIds?.() ?? [];
              const all: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
              let target = -1;
              for (const a of all) {
                if (a.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
                const ref: string | undefined = a.metadata?.referencedImageId;
                const idx = ref ? ids.indexOf(ref) : -1;
                if (idx >= 0 && (target === -1 || idx < target)) target = idx;
              }
              matchedViewport.setImageIdIndex(target >= 0 ? target : currentIdx);
              matchedViewport.render?.();
            } catch (e) {
              logger.debug("[StoneOverlay] jump-to-contour skipped:", e);
            }

            // Load companion-plane masks onto their sibling viewports so the
            // stone shows on every annotated view (axial/sag/cor), not just the
            // primary. Each companion mask lives on a different series; find the
            // viewport showing that series and load it there.
            const companionUrls = annotationsQuery.data?.companion_mask_urls;
            const anns = annotationsQuery.data?.annotations;
            if (companionUrls && anns) {
              const compSeries = new Map<string, string>();
              for (const ann of anns) {
                for (const comp of ann.companion_annotations ?? []) {
                  compSeries.set(comp.companion_id, comp.series_instance_uid);
                }
              }
              for (const [compId, compUrl] of Object.entries(companionUrls)) {
                const compSeriesUid = compSeries.get(compId);
                if (!compSeriesUid) {
                  logger.warn(`[StoneOverlay] companion ${compId}: no series uid in annotation`);
                  continue;
                }
                let compAttempts = 0;
                const compPoll = setInterval(() => {
                  if (cancelled) {
                    clearInterval(compPoll);
                    return;
                  }
                  compAttempts++;
                  let compVp: any = null;
                  let compEl: HTMLElement | null = null;
                  try {
                    const cs = getCornerstone();
                    const compEngines: any[] = cs.getRenderingEngines?.() ?? [];
                    for (const engine of compEngines) {
                      for (const vp of (engine.getViewports?.() ?? []) as any[]) {
                        const ids: string[] = vp.getImageIds?.() ?? [];
                        if (
                          ids.length > 0 &&
                          vp.getImageData?.()?.imageData &&
                          ids[0]?.includes(compSeriesUid.split("::")[0])
                        ) {
                          compVp = vp;
                          compEl = vp.element ?? null;
                          break;
                        }
                      }
                      if (compVp) break;
                    }
                  } catch {
                    /* cornerstone not ready yet — retry */
                  }
                  if (!compVp || !compEl) {
                    if (compAttempts >= maxAttempts) {
                      clearInterval(compPoll);
                      logger.warn(
                        `[StoneOverlay] companion series ${compSeriesUid} not shown in any viewport — companion mask not loaded`,
                      );
                    }
                    return;
                  }
                  clearInterval(compPoll);
                  const compIdx = compVp.getCurrentImageIdIndex?.() ?? 0;
                  loadMaskAsContours(compUrl, segmentationVolumeId, compVp, compEl, {
                    labelOrders: companionLabelOrders(anns, compId),
                  })
                    .then((cn: number) => {
                      logger.info(
                        `[StoneOverlay] loaded ${cn} companion contour(s) on series …${compSeriesUid.slice(-8)}`,
                      );
                      try {
                        compVp.setImageIdIndex(compIdx);
                        compVp.render?.();
                      } catch {
                        /* ignore */
                      }
                    })
                    .catch((ce: unknown) =>
                      logger.warn(`[StoneOverlay] companion ${compId} load failed:`, ce),
                    );
                }, 200);
                companionPolls.push(compPoll);
              }
            }
          })
          .catch((e) => {
            maskLoadedRef.current = false; // allow a retry on next data/gate change
            logger.error("[StoneOverlay] loadMaskAsContours failed:", e);
          });
      } catch (e) {
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          logger.error("[StoneOverlay] viewport poll error:", e);
        }
      }
    }, 200);

    return () => {
      cancelled = true;
      clearInterval(poll);
      companionPolls.forEach(clearInterval);
    };
  }, [enabled, studyUid, annotationsQuery.data, annotations, segmentationVolumeId]);
}
